import React, {FC, useCallback} from 'react';
import {useField} from 'formik';
import AutocompleteInput, {AutocompleteInputProps} from '../inputs/AutocompleteInput';
import {OptionsProp} from '../../../../constants/types';

export interface AutocompleteFieldProps extends Omit<AutocompleteInputProps, 'id' | 'value' | 'onChange'> {
  id?: string,
  fieldDescription?: string,
  onChangeCallback?: (value) => void
}

const multiSelectValueParser = (
    selected: Nullable<OptionsProp | OptionsProp[]> = []
) => {
  if (!Array.isArray(selected)) {
    return [];
  }

  return selected.map((option) => option?.value) || [];
};

const AutocompleteField: FC<AutocompleteFieldProps> = ({
  name,
  id,
  label,
  options,
  required,
  disabled,
  onChangeCallback,
  onFocus,
  onBlur,
  apiValidationError,
  multiple,
  fieldDescription,
  disableClearable,
  disableCloseOnSelect,
  withNewEntity,
  size,
  disablePortal,
  maxMenuHeight
}) => {
  const [
    {value},
    {error, touched},
    {setTouched, setValue}
  ] = useField(name);

  const handleChangeSingleSelect = useCallback((newValue) => {
    const value = newValue?.value || '';

    if (onChangeCallback) {
      return onChangeCallback(value);
    }

    setTouched(true);
    setValue(value);
  }, []);

  const handleChangeMultiSelect = useCallback((newValue) => {
    if (onChangeCallback) {
      return onChangeCallback(newValue);
    }

    const value = multiSelectValueParser(newValue);

    setValue(value);
    setTouched(true);
  }, []);

  const handleChange = useCallback((_, newValue) => {
    if (!multiple) {
      return handleChangeSingleSelect(newValue);
    }

    handleChangeMultiSelect(newValue);
  }, []);

  return (
    <>
      <AutocompleteInput
        id={id || name}
        options={options}
        value={value}
        onChange={handleChange}
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        error={error}
        touched={touched}
        onFocus={onFocus}
        onBlur={onBlur}
        apiValidationError={apiValidationError}
        multiple={multiple}
        disableClearable={disableClearable}
        disableCloseOnSelect={disableCloseOnSelect}
        withNewEntity={withNewEntity}
        size={size}
        disablePortal={disablePortal}
        maxMenuHeight={maxMenuHeight}
      />
      {fieldDescription && (
        <p className="field-description">{fieldDescription}</p>
      )}
    </>
  );
};

export default AutocompleteField;
